import { useEffect, useRef } from "react";
import {
  queryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import {
  cancelSubscription,
  getSubscription,
  getWallet,
  isApiError,
  listInvoices,
  listModelRates,
  listPackages,
  listTransactions,
  listUsage,
  listWalletAlerts,
  startSubscription,
  startTopUpCheckout,
} from "@empite/api-client";
import type {
  AlertList,
  CancelSubscriptionResult,
  CheckoutRequest,
  CheckoutResult,
  InvoiceList,
  ModelRateList,
  PackageOfferList,
  SubscribeRequest,
  SubscribeResult,
  Subscription,
  SubscriptionState,
  TransactionList,
  Transport,
  UsagePage,
  Wallet,
} from "@empite/api-client";
import { useTransport } from "@empite/session";
import type { PortalApplication } from "./applications";

/**
 * The billing query keys.
 *
 * Every key starts with the application id because that is the boundary the
 * money itself respects: credit in one application's wallet cannot be spent
 * in another, so nothing cached under one id may ever be reused for a second.
 * `application()` is the invalidation unit — a completed top-up changes the
 * balance, the transactions and the invoices of exactly one application.
 */
export const billingKeys = {
  all: ["billing"] as const,
  application: (applicationID: string) => ["billing", applicationID] as const,
  wallet: (applicationID: string) => ["billing", applicationID, "wallet"] as const,
  usage: (applicationID: string, limit: number) =>
    ["billing", applicationID, "usage", limit] as const,
  transactions: (applicationID: string, limit: number) =>
    ["billing", applicationID, "transactions", limit] as const,
  invoices: (applicationID: string, limit: number) =>
    ["billing", applicationID, "invoices", limit] as const,
  alerts: (applicationID: string, limit: number) =>
    ["billing", applicationID, "alerts", limit] as const,
  packages: (applicationID: string) => ["billing", applicationID, "packages"] as const,
  modelRates: (applicationID: string) => ["billing", applicationID, "model-rates"] as const,
  subscription: (applicationID: string) => ["billing", applicationID, "subscription"] as const,
};

/** How many rows a screen asks for. These are lists a person reads on the way
 *  to a question, not an export — the answer to "show me everything" is a
 *  statement download, which no route serves yet. */
export const RECENT_LIMIT = 25;

/**
 * A 404 here means "this customer has no wallet for that application", which
 * is an ordinary state on a dashboard listing several applications — not a
 * failure. Every other status still throws.
 *
 * It reads as 404 rather than as an empty wallet because the id in the path is
 * a filter and never authority: somebody else's application id answers exactly
 * the same 404, so there is no version of this call that could leak whether
 * the application exists.
 */
function absent(err: unknown): boolean {
  return isApiError(err) && err.status === 404;
}

async function orNull<T>(load: () => Promise<T>): Promise<T | null> {
  try {
    return await load();
  } catch (err) {
    if (absent(err)) {
      return null;
    }
    throw err;
  }
}

export function walletQuery(transport: Transport, applicationID: string) {
  return queryOptions({
    queryKey: billingKeys.wallet(applicationID),
    queryFn: () => orNull(async () => (await getWallet(transport, { applicationID })).wallet),
    // A balance moves whenever a request is priced, so it is never fresh for
    // long; 15s is short enough that a top-up shows up on its own and long
    // enough that a dashboard of six wallets is not six refetches per render.
    staleTime: 15_000,
  });
}

export function usageQuery(transport: Transport, applicationID: string, limit = RECENT_LIMIT) {
  return queryOptions({
    queryKey: billingKeys.usage(applicationID, limit),
    queryFn: () => orNull(() => listUsage(transport, { applicationID, limit })),
    staleTime: 15_000,
  });
}

export function transactionsQuery(
  transport: Transport,
  applicationID: string,
  limit = RECENT_LIMIT,
) {
  return queryOptions({
    queryKey: billingKeys.transactions(applicationID, limit),
    queryFn: () => orNull(() => listTransactions(transport, { applicationID, limit })),
    staleTime: 15_000,
  });
}

export function invoicesQuery(transport: Transport, applicationID: string, limit = RECENT_LIMIT) {
  return queryOptions({
    queryKey: billingKeys.invoices(applicationID, limit),
    queryFn: () => orNull(() => listInvoices(transport, { applicationID, limit })),
    staleTime: 15_000,
  });
}

/**
 * The threshold crossings raised against one application's wallet.
 *
 * It sits under the same `application()` prefix as the balance for a reason
 * the banner depends on: a completed top-up invalidates that prefix, so the
 * alerts are re-read by the same act that fixed them. A screen that kept
 * saying "you are out of credit" after the credit landed would be the one
 * failure this banner cannot afford.
 *
 * The list includes RESOLVED crossings — the platform serves them so the table
 * can be a history — so every caller that draws urgency from it has to filter
 * for the open ones itself; see `billing-common.tsx:isOpenAlert`.
 *
 * The same 15s staleness as the balance, deliberately. An alert is a statement
 * about a balance, and the two disagreeing on screen is worse than either
 * being a few seconds old.
 */
export function alertsQuery(transport: Transport, applicationID: string, limit = RECENT_LIMIT) {
  return queryOptions({
    queryKey: billingKeys.alerts(applicationID, limit),
    queryFn: () => orNull(() => listWalletAlerts(transport, { applicationID, limit })),
    staleTime: 15_000,
  });
}

/**
 * The plans on sale for one application.
 *
 * Five minutes rather than the fifteen seconds every other read here uses. A
 * balance moves whenever a request is priced; a price list moves when an
 * operator publishes a new version, which is a deliberate and rare act that
 * nothing the customer does can trigger. Re-asking for it every fifteen
 * seconds would be polling a catalogue for an event that arrives by email.
 *
 * A subscribe pins the version id the card was rendered from, so a re-price
 * that lands while a stale list is on screen is a refused subscribe rather
 * than a silent charge at the new price — which is what makes the long stale
 * time safe rather than merely cheap.
 */
export function packagesQuery(transport: Transport, applicationID: string) {
  return queryOptions({
    queryKey: billingKeys.packages(applicationID),
    queryFn: () => orNull(() => listPackages(transport, { applicationID })),
    staleTime: 5 * 60_000,
  });
}

/**
 * The per-model sell rates for one application.
 *
 * The same five minutes the plans list uses, and for the same reason: a rate
 * moves when an operator publishes a price version, which is a deliberate and
 * rare act nothing the customer does can trigger.
 *
 * Every figure in the response was computed server-side by the pricing code
 * that charges. Nothing here multiplies a rate by a markup, and nothing may
 * start to: `Number("0.000015")` is how a per-million rate stops being a rate,
 * and a page that derived its own sell price would be right until one of the
 * five rounding steps moved.
 */
export function modelRatesQuery(transport: Transport, applicationID: string) {
  return queryOptions({
    queryKey: billingKeys.modelRates(applicationID),
    queryFn: () => orNull(() => listModelRates(transport, { applicationID })),
    staleTime: 5 * 60_000,
  });
}

export function useModelRates(applicationID: string): UseQueryResult<ModelRateList | null> {
  return useQuery(modelRatesQuery(useTransport(), applicationID));
}

/** How often an unconfirmed subscription is re-read while the customer waits. */
export const PENDING_POLL_MS = 3_000;

/**
 * How long that polling keeps going.
 *
 * An `incomplete` subscription is not always one whose webhook is a second
 * away — it is also what a customer who opened the hosted page and closed the
 * tab leaves behind, and that row stays `incomplete` until it expires. Polling
 * it forever would be a request every three seconds for as long as the screen
 * is open, so the wait is bounded and the copy changes rather than the spinner
 * running out of patience silently.
 */
export const PENDING_WINDOW_MS = 15 * 60_000;

/**
 * The poll interval for one subscription: how long until this row is worth
 * re-reading, or `false` when it is not.
 *
 * Only `incomplete` is waited on. `past_due` is a renewal the provider is
 * still retrying over days, not seconds — polling it would never stop.
 */
export function pendingPoll(
  subscription: Subscription | null | undefined,
  now = Date.now(),
): number | false {
  if (!subscription || subscription.status !== "incomplete") {
    return false;
  }
  return Date.parse(subscription.created_at) + PENDING_WINDOW_MS > now ? PENDING_POLL_MS : false;
}

/**
 * What the customer is on, polled while it is still being confirmed.
 *
 * The redirect back from the hosted page is not proof of payment — only the
 * provider's webhook is, and it arrives at the worker a moment later. So this
 * query re-asks on a timer while the subscription is `incomplete`, which is
 * what turns "we are confirming your payment" into a live plan without the
 * customer being told to refresh.
 */
export function subscriptionQuery(transport: Transport, applicationID: string) {
  return queryOptions({
    queryKey: billingKeys.subscription(applicationID),
    queryFn: () => orNull(() => getSubscription(transport, { applicationID })),
    staleTime: 15_000,
    refetchInterval: (query) => pendingPoll(query.state.data?.subscription),
  });
}

export function useWallet(applicationID: string): UseQueryResult<Wallet | null> {
  return useQuery(walletQuery(useTransport(), applicationID));
}

export function useUsage(applicationID: string, limit = RECENT_LIMIT): UseQueryResult<UsagePage | null> {
  return useQuery(usageQuery(useTransport(), applicationID, limit));
}

export function useTransactions(
  applicationID: string,
  limit = RECENT_LIMIT,
): UseQueryResult<TransactionList | null> {
  return useQuery(transactionsQuery(useTransport(), applicationID, limit));
}

export function useInvoices(
  applicationID: string,
  limit = RECENT_LIMIT,
): UseQueryResult<InvoiceList | null> {
  return useQuery(invoicesQuery(useTransport(), applicationID, limit));
}

export function useAlerts(applicationID: string, limit = RECENT_LIMIT): UseQueryResult<AlertList | null> {
  return useQuery(alertsQuery(useTransport(), applicationID, limit));
}

export function usePackages(applicationID: string): UseQueryResult<PackageOfferList | null> {
  return useQuery(packagesQuery(useTransport(), applicationID));
}

/**
 * The current subscription, and the one read on this screen with a side
 * effect attached.
 *
 * The moment an `incomplete` subscription stops being incomplete, the worker
 * that flipped it has also granted the first cycle's allowance — the balance,
 * the transactions and the receipts on the same screen are all now stale by
 * exactly one webhook. Nothing else on the page knows that happened, because
 * nothing else is watching this status. So the transition is watched HERE and
 * the application's whole key is invalidated once, rather than every other
 * query on the screen being put on a timer of its own.
 *
 * It compares the status against the previous render rather than firing on
 * "is active": the invalidation has to happen on the EDGE. Keyed on the value,
 * a screen opened on an already-active subscription would invalidate on first
 * paint for nothing, and — since the subscription query sits under the same
 * key prefix — that refetch would arrive as another render with the same
 * status and do it again.
 */
export function useSubscription(applicationID: string): UseQueryResult<SubscriptionState | null> {
  const queries = useQueryClient();
  const result = useQuery(subscriptionQuery(useTransport(), applicationID));
  const status = result.data?.subscription?.status;
  const previous = useRef(status);

  useEffect(() => {
    const before = previous.current;
    previous.current = status;
    if (before === "incomplete" && status !== undefined && status !== "incomplete") {
      void queries.invalidateQueries({ queryKey: billingKeys.application(applicationID) });
    }
  }, [applicationID, queries, status]);

  return result;
}

/**
 * useStartSubscription opens a hosted checkout page, and — exactly like
 * {@link useStartTopUp} — does NOT navigate to it. Same rule, same reason: a
 * payment page the customer did not press is indistinguishable from one that
 * opened itself.
 *
 * The application id is an argument to the hook rather than a field on the
 * body, because the subscribe route carries it in the PATH: the top-up
 * checkout is a suite-wide collection that names its application, this is one
 * application's single subscription.
 *
 * On success it invalidates the whole application key, not just the
 * subscription. The route writes the subscription as `incomplete` and grants
 * nothing, so the balance has not moved — but a replayed idempotency key can
 * return a subscription that has been live for a month, and that one's wallet
 * is worth re-reading.
 */
export function useStartSubscription(
  applicationID: string,
): UseMutationResult<SubscribeResult, unknown, SubscribeRequest> {
  const transport = useTransport();
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (body: SubscribeRequest) => startSubscription(transport, { applicationID, body }),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: billingKeys.application(applicationID) });
    },
  });
}

/**
 * Ending the agreement, at the end of the period already paid for.
 *
 * The currency is the WALLET's, exactly as the subscribe sends it: the route
 * resolves the subscription through a wallet, and a customer holding two
 * wallets for one application would otherwise cancel whichever one the
 * deployment's default names.
 *
 * There is no argument and no body. The route addresses the one live
 * subscription this application has — the path is singular because
 * subscriptions_live_key makes it singular — so a subscription id in the
 * request would be a second, weaker way of naming the same row.
 *
 * On success the whole application key is invalidated rather than the
 * subscription alone, because a cancel can move two other things on this
 * screen. An agreement whose hosted page was never completed ends OUTRIGHT,
 * which releases the one-live-subscription slot the plans list renders its
 * buttons from; and one that ends at the period end turns the balance card's
 * "resets" date into the date that credit disappears for good.
 */
export function useCancelSubscription(
  applicationID: string,
  currency: string | undefined,
): UseMutationResult<CancelSubscriptionResult, unknown, void> {
  const transport = useTransport();
  const queries = useQueryClient();
  return useMutation({
    mutationFn: () => cancelSubscription(transport, { applicationID, currency }),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: billingKeys.application(applicationID) });
    },
  });
}

/**
 * Re-asking the platform for everything this application's money is made of.
 *
 * It exists for the one state on this screen that nothing else resolves: a
 * `past_due` subscription is a renewal the PROVIDER is retrying on its own
 * schedule, over days. There is no route that makes it retry sooner — this
 * platform records dunning and does not drive it — so the honest action to
 * offer is to look again, and the honest thing to call it is checking rather
 * than retrying.
 *
 * `incomplete` is not why this is here: that one polls itself. Polling
 * `past_due` on the same timer would be a request every three seconds for as
 * long as the tab is open, against an event that may be two days away.
 *
 * A mutation rather than a bare `refetch` because the whole application key is
 * the unit — the retry that succeeds moves the balance and writes a receipt as
 * well as flipping the status — and because `isPending` is then the button's
 * busy state for free, resolving when the refetches it triggered have settled.
 */
export function useRefreshBilling(applicationID: string): UseMutationResult<void, unknown, void> {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: () => queries.invalidateQueries({ queryKey: billingKeys.application(applicationID) }),
  });
}

/**
 * One query per application, fanned out rather than aggregated server-side.
 *
 * There is no route that sums several applications, and there should not be:
 * the sum is a DISPLAY, while every read that money depends on stays scoped to
 * one application id. Fanning out in the browser keeps that true — the screen
 * adds up numbers it was given, it never asks the platform for a total that
 * would have to cross a wallet boundary to compute.
 */
export function useWalletsAcross(
  applications: PortalApplication[],
): Array<UseQueryResult<Wallet | null>> {
  const transport = useTransport();
  return useQueries({
    queries: applications.map((app) => walletQuery(transport, app.id)),
  });
}

export function useUsageAcross(
  applications: PortalApplication[],
  limit = RECENT_LIMIT,
): Array<UseQueryResult<UsagePage | null>> {
  const transport = useTransport();
  return useQueries({
    queries: applications.map((app) => usageQuery(transport, app.id, limit)),
  });
}

/**
 * The same fan-out for top-up receipts, so the wallets screen can show
 * "recent top-ups" across the suite.
 *
 * It reuses `invoicesQuery` rather than declaring a key of its own: a receipt
 * still belongs to exactly one application, and a merged list cached under a
 * key of its own would be a second copy of rows a completed top-up already
 * invalidates through `billingKeys.application()`. The merge is a DISPLAY, the
 * same way the wallet totals are.
 */
export function useInvoicesAcross(
  applications: PortalApplication[],
  limit = RECENT_LIMIT,
): Array<UseQueryResult<InvoiceList | null>> {
  const transport = useTransport();
  return useQueries({
    queries: applications.map((app) => invoicesQuery(transport, app.id, limit)),
  });
}

/**
 * The same fan-out for wallet alerts, so the wallets screen can warn about
 * every application at once.
 *
 * Fanned out per application rather than answered by one suite-wide route, for
 * exactly the reason {@link useWalletsAcross} is: an alert is raised against
 * ONE wallet, and a merged list is a display. Sharing `alertsQuery`'s key also
 * means this screen and one wallet's own screen read the same cache entry, so
 * they can never disagree about whether that wallet is in trouble.
 */
export function useAlertsAcross(
  applications: PortalApplication[],
  limit = RECENT_LIMIT,
): Array<UseQueryResult<AlertList | null>> {
  const transport = useTransport();
  return useQueries({
    queries: applications.map((app) => alertsQuery(transport, app.id, limit)),
  });
}

/**
 * useStartTopUp opens a hosted checkout page. It does NOT navigate to it.
 *
 * The URL comes back from our own platform, but the screen still renders it as
 * something the customer presses rather than a redirect that happens to them:
 * a payment page a person did not choose to open is indistinguishable from one
 * that opened itself, and this portal already refuses to auto-navigate
 * anywhere on the strength of a response body.
 */
export function useStartTopUp(): UseMutationResult<CheckoutResult, unknown, CheckoutRequest> {
  const transport = useTransport();
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (body: CheckoutRequest) => startTopUpCheckout(transport, { body }),
    // The pending top-up is already a row the invoices list should show, even
    // though no credit has landed yet — that row is how a customer whose
    // payment failed finds out it did.
    onSuccess: async (result) => {
      await queries.invalidateQueries({ queryKey: billingKeys.application(result.top_up.application_id) });
    },
  });
}
